import axios from "axios";

// ─── Backend URL resolution — Phase 36 Batch V hotfix ─────────────────────
//
// Why this isn't `const BACKEND_URL = process.env.REACT_APP_BACKEND_URL`:
//
// The prod build was compiled with REACT_APP_BACKEND_URL pointing at the
// Emergent-issued API hostname (e.g. `https://invest-phase1.emergent.host`)
// while the user-facing custom domain is `https://runidea.ai`. Same backend
// pod, two hostnames. With the env-var literal:
//
//     baseURL = "https://invest-phase1.emergent.host/api"
//
// every API call from runidea.ai is cross-origin + credentialed → triggers
// a CORS preflight → backend CORS_ORIGINS env var on prod did NOT include
// runidea.ai → preflight returned 400 with no Access-Control-Allow-Origin
// → browser blocked the call → Google sign-in callback (initiated from
// runidea.ai/auth/callback) failed with "Couldn't complete sign-in".
//
// Fix: if at runtime the page origin is NOT an Emergent platform domain
// (i.e. we're on a custom domain like runidea.ai), prefer that origin as
// the backend host. Custom domains always self-route /api/* to the same
// backend pod (verified for runidea.ai — /api/health on both hostnames
// returns the same pod timestamp). Same-origin → no CORS preflight at
// all → robust by construction.
//
// On preview AND on the Emergent-issued hostname, the build-time env var
// stays the source of truth (no behavior change). This is intentionally
// conservative — we never override the env var unless we're confident
// the runtime origin is a customer-owned domain.

const BUILD_TIME_BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function _resolveBackendUrl() {
  // SSR / Node test environment — there's no `window`. Trust the env var.
  if (typeof window === "undefined" || !window.location) {
    return BUILD_TIME_BACKEND;
  }
  const runtimeOrigin = window.location.origin || "";
  if (!runtimeOrigin) return BUILD_TIME_BACKEND;
  let runtimeHost;
  let buildHost;
  try {
    runtimeHost = new URL(runtimeOrigin).host;
    buildHost = BUILD_TIME_BACKEND ? new URL(BUILD_TIME_BACKEND).host : "";
  } catch {
    // Malformed URL somewhere — bail to the env var.
    return BUILD_TIME_BACKEND;
  }
  // Already same-host — env var is fine, return it verbatim so the
  // protocol/path normalisation matches the build's intent.
  if (buildHost && buildHost === runtimeHost) return BUILD_TIME_BACKEND;
  // If we're on a recognised Emergent platform hostname, the env var is
  // the source of truth (covers preview deployments + the *.emergent.host
  // platform-issued URLs). We do NOT override these.
  const isEmergentPlatform = /\.(emergent\.host|emergentagent\.com)$/i.test(runtimeHost);
  if (isEmergentPlatform) return BUILD_TIME_BACKEND;
  // Custom domain (e.g. runidea.ai). Prefer same-origin to avoid CORS.
  return runtimeOrigin;
}

const BACKEND_URL = _resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

// Exposed for diagnostics — surfaces in the browser console as
// `window.__runidea_backend` and helps support triage cross-origin bugs
// in the field without having to read the minified bundle.
if (typeof window !== "undefined") {
  try {
    window.__runidea_backend = { resolved: BACKEND_URL, build: BUILD_TIME_BACKEND };
  } catch { /* ignore */ }
}

// Axios instance: always send cookies (httpOnly session) on cross-origin/same-origin calls
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Phase 14 Wave 3 — maintenance-mode detection. When backend returns
// 503 with `{maintenance: true}`, dispatch a window event so the
// `<MaintenanceOverlay />` component can render the full-page overlay.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status === 503 && body && body.maintenance === true) {
      try {
        window.dispatchEvent(new CustomEvent("runidea:maintenance", {
          detail: { message: body.message || "" },
        }));
      } catch { /* SSR or older browser */ }
    }
    return Promise.reject(err);
  },
);

// Phase 36 Dashboard P0 follow-up #4 — centralized 401 auto-retry.
//
// Many protected endpoints intermittently 401 on first navigation
// because the httpOnly session cookie hasn't yet been set by the
// browser when React fires its initial `useEffect` data-loads. The
// retry waits a short delay (cookie now set) and re-issues the SAME
// request via the SAME axios instance, so consumers (Portfolio /
// Reports / Copilot / Settings / DealDetail / 18 admin pages) get
// transparent recovery without per-hook plumbing.
//
// Skipped for:
//   • auth endpoints themselves (login / me / signup) — those 401s
//     are real bad-credential / not-logged-in signals that must
//     propagate to the UI for redirect logic.
//   • requests already retried once (avoid infinite loop).
//
// If the second attempt also 401s, the error propagates as before
// and the page renders its existing error / empty / login UX.
const AUTH_RACE_RETRY_DELAY_MS = 1500;
const AUTH_PASSTHROUGH = ["/auth/login", "/auth/me", "/auth/signup", "/auth/logout", "/auth/forgot-password", "/auth/reset-password"];

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    if (status !== 401) return Promise.reject(err);
    const config = err.config;
    if (!config || config._retry401) return Promise.reject(err);
    const url = config.url || "";
    if (AUTH_PASSTHROUGH.some((p) => url.includes(p))) {
      return Promise.reject(err);
    }
    config._retry401 = true;
    await new Promise((res) => setTimeout(res, AUTH_RACE_RETRY_DELAY_MS));
    return api(config);
  },
);

export default api;
